import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CommsMessage } from '@interfaces/ICommsApi';

const NEAR_BOTTOM_THRESHOLD_PX = 80;

interface IUseScrollManagerOptions {
  messages: CommsMessage[];
  currentUserId: string;
  hasMore: boolean;
  loadMore: () => void;
}

export function useScrollManager({ messages, currentUserId, hasMore, loadMore }: IUseScrollManagerOptions) {
  // The scrolling viewport (`.root`) and the element wrapping all message
  // rows inside it (`.list`). We measure the viewport's scroll position but
  // observe the inner content's size, since the viewport's own box is fixed.
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);

  // Invisible marker at the very top of the list. When it scrolls into view,
  // we load the next (older) page of history.
  const loadOlderTriggerRef = useRef<HTMLDivElement>(null);

  // True once we've done the one-time jump-to-bottom after history first loads.
  const hasDoneInitialScrollRef = useRef(false);

  // Before loading older messages, we save how far the user was from the
  // bottom; after the older messages prepend (and push everything down), we
  // restore that distance so the user keeps their place. null = no restore pending.
  const savedDistanceFromBottomRef = useRef<number | null>(null);

  // Whether the user is currently scrolled to (or near) the bottom.
  const isAtBottomRef = useRef(true);

  // The id of the last message we've already reacted to. When the real last
  // id differs from this, a genuinely new message has arrived.
  const lastHandledMessageIdRef = useRef<string | null>(null);

  // How much to bump the "new messages" badge by on the next render. Staged in
  // a ref so the layout effect doesn't call setState directly (cascading renders).
  const pendingBadgeIncrementRef = useRef(0);

  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessageCount, setNewMessageCount] = useState(0);

  // Track whether user is near the bottom; clear badge on return.
  useEffect(() => {
    const viewport = scrollViewportRef.current;
    if (!viewport) return;

    function onScroll() {
      const distanceFromBottom = viewport!.scrollHeight - viewport!.scrollTop - viewport!.clientHeight;
      const atBottom = distanceFromBottom < NEAR_BOTTOM_THRESHOLD_PX;
      if (atBottom === isAtBottomRef.current) return;
      isAtBottomRef.current = atBottom;
      setIsAtBottom(atBottom);
      if (atBottom) setNewMessageCount(0);
    }

    viewport.addEventListener('scroll', onScroll, { passive: true });
    return () => viewport.removeEventListener('scroll', onScroll);
  }, []);

  // All scroll management in one layout effect:
  //   1. Restore position after older messages are prepended.
  //   2. Jump to bottom on initial load.
  //   3. Auto-scroll or badge on new live message.
  //
  // Keyed on BOTH length and last message id:
  //   - length change handles new messages and history prepends.
  //   - id change at same length handles the optimistic→real swap: when an
  //     attachment message is sent, the optimistic row (id=clientMessageId,
  //     attachments:[]) is replaced in-place by the confirmed message
  //     (id=serverId, attachments:[{...}]). The length doesn't grow, but the
  //     last id changes — without this dep, AttachmentView renders at non-zero
  //     height with no scroll to bottom.
  //   Edits and soft-deletes never change the message id so they don't re-run.
  const lastMessageId = messages[messages.length - 1]?.id;
  useLayoutEffect(() => {
    const viewport = scrollViewportRef.current;
    if (!viewport) return;

    if (savedDistanceFromBottomRef.current !== null) {
      viewport.scrollTop = viewport.scrollHeight - savedDistanceFromBottomRef.current;
      savedDistanceFromBottomRef.current = null;
      return;
    }

    if (!hasDoneInitialScrollRef.current && messages.length > 0) {
      viewport.scrollTop = viewport.scrollHeight;
      hasDoneInitialScrollRef.current = true;
      lastHandledMessageIdRef.current = messages[messages.length - 1]?.id ?? null;
      return;
    }

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.id === lastHandledMessageIdRef.current) return;
    lastHandledMessageIdRef.current = lastMessage.id;

    if (isAtBottomRef.current) {
      viewport.scrollTop = viewport.scrollHeight;
    } else if (lastMessage.authorId !== currentUserId) {
      pendingBadgeIncrementRef.current += 1;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, lastMessageId]);

  useEffect(() => {
    if (pendingBadgeIncrementRef.current > 0) {
      setNewMessageCount((c) => c + pendingBadgeIncrementRef.current);
      pendingBadgeIncrementRef.current = 0;
    }
  }, [messages.length, lastMessageId]);

  // Keep the view pinned to the bottom while the user is already there.
  // The initial scroll-to-bottom above runs once on history load, but
  // attachment rows (images/voice) render a placeholder first and swap in
  // the real element later via useAttachmentUrl — and native <img> decoding
  // reflows again. Each of those height changes happens AFTER the initial
  // scroll, pushing the latest message below the fold. A ResizeObserver on
  // the content re-pins to bottom on every such growth, but only when the
  // user hasn't scrolled away (isAtBottomRef) — so reading history (which
  // grows the top) and the "new messages" badge are left untouched.
  const hasMessages = messages.length > 0;
  useEffect(() => {
    const content = scrollContentRef.current;
    const viewport = scrollViewportRef.current;
    if (!content || !viewport) return;

    const observer = new ResizeObserver(() => {
      if (isAtBottomRef.current) viewport.scrollTop = viewport.scrollHeight;
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [hasMessages]);

  // Save the user's distance from the bottom BEFORE asking for older messages,
  // so the layout effect can restore it once they prepend.
  const handleLoadMore = useCallback(() => {
    if (savedDistanceFromBottomRef.current !== null) return;
    const viewport = scrollViewportRef.current;
    if (viewport) savedDistanceFromBottomRef.current = viewport.scrollHeight - viewport.scrollTop;
    loadMore();
  }, [loadMore]);

  // Watch the top trigger; when it scrolls into view, load older history.
  // `root` must be the chat's scroll viewport — without it the browser checks
  // against the document viewport, which fires immediately on mount.
  useEffect(() => {
    const trigger = loadOlderTriggerRef.current;
    const root = scrollViewportRef.current;
    if (!trigger || !root || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) handleLoadMore();
      },
      { root, threshold: 0.1 },
    );
    observer.observe(trigger);
    return () => observer.disconnect();
  }, [hasMore, handleLoadMore]);

  const scrollToBottom = useCallback(() => {
    const viewport = scrollViewportRef.current;
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
    setNewMessageCount(0);
  }, []);

  return {
    scrollViewportRef,
    scrollContentRef,
    loadOlderTriggerRef,
    isAtBottom,
    newMessageCount,
    scrollToBottom,
  };
}
